import './App.css';
import './SearchFeatureContainer'
import Footer from './Footer';
import { ConfigProvider } from "./ConfigContext";
import { validateConfig } from './utils/generalUtils';
import { fetchKeywordWeight, updateKeywordWeight } from './services/apiService';
import React, { useState, useEffect } from 'react';
import { useAuth } from "react-oidc-context";
import { handleCognitoResponse } from './services/authService.ts';
import Sidebar from './Sidebar';
import SearchFeatureContainer from './SearchFeatureContainer';

function isAuthLoading(auth) {
  return auth.activeNavigator === "signinRedirect" || auth.isLoading;
}

async function loadConfig() {
  const res = await fetch("/config.json", {
    cache: "no-store"
  });
  if (!res.ok) {
    throw new Error("config.json not found or not accessible");
  }
  const configJson = await res.json();
  if (!validateConfig(configJson)) {
    throw new Error("Missing required config values in config.json");
  }

  return configJson;
}

function App() {
  const auth = useAuth();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sidebar Menu
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [keywordWeight, setKeywordWeight] = useState(0.3)
  const standardAvailableFields = ["broadcast_series_title", "title", "teaser_text"];

  // Request config (passed to api service) -> editable via sidebar
  const [requestConfig, setRequestConfig] = useState({
    selectedFields: standardAvailableFields,
    fieldBoosts: {},
    filters: [],
    notFilters: [],
    sliderValue: 7000,
    maxResults: 10,
  });

  useEffect(() => {
    if (isAuthLoading(auth)) {
      return;
    }
    if (!isAuthLoading(auth)) {
      if (auth.isAuthenticated) {
        setIsModalOpen(false);
      } else {
        handleCognitoResponse()
          .then((isAuthenticated) => {
            if (isAuthenticated) {
              setIsModalOpen(false);
            } else {
              setIsModalOpen(true);
            }
          })
          .catch((err) => {
            console.error("Error handling Cognito response:", err);
            setIsModalOpen(true);
          });
      }
    }
  }, [auth, auth.isLoading, auth.isAuthenticated, auth.activeNavigator]);

  useEffect(() => {
    async function init() {
      setError(null)
      try {
        const configJson = await loadConfig();
        setConfig(configJson);
        const keywordWeight = await fetchKeywordWeight(configJson);
  
        if (keywordWeight != null) {
          setKeywordWeight(keywordWeight);
        }
      } catch (err) {
        console.warn("⚠️ Initialisierung fehlgeschlagen:", err);
        setError(err.message || err)
      }
    }
  
    init();
  }, []);

  const handleLogin = () => {
    const CLIENT_ID = config.CLIENT_ID;
    const COGNITO_DOMAIN = config.COGNITO_DOMAIN;
    const redirectUri = `${window.location.origin}`;
    const loginUrl = `${COGNITO_DOMAIN}/login?client_id=${CLIENT_ID}&response_type=code&scope=email+openid+phone&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = loginUrl;
  };

  const toggleSidebar = async () => {
    setIsSidebarOpen(!isSidebarOpen);
    try {
      const keywordWeight = await fetchKeywordWeight(config);

      if (keywordWeight != null) {
        setRequestConfig((prev) => ({
          ...prev,
          keywordWeight: keywordWeight,
        }));
      }
    } catch (err) {
      console.warn("⚠️ Konnte keyword_weight nicht vom Server laden:", err);
    }
  };

  const handleUpdateKeywordWeight = async () => {
    try {
      await updateKeywordWeight(config, keywordWeight)
      console.log(`Gewichtung gesetzt: ${keywordWeight}`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    }
    catch (err) {
      console.error('Fehler beim Setzen der Gewichtung:', err)
    }
  };

  if (error) {
    return (
      <div className="fatal-error">
        <h2>❌ Fehler beim Laden der Anwendung</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (isAuthLoading(auth) || !config) {
    return <div>Loading...</div>;
  }

  return (
    <ConfigProvider config={config}>
      <div className="App">
        {isModalOpen && (
          <div className="modal">
            <div className="modal-content">
              <h2>Please Log In</h2>
              <button onClick={handleLogin}>Log In</button>
            </div>
          </div>
        )}
        {!isModalOpen && (
          <>
            <div style={{ margin: '2em 0' }}>
            </div>
            <Sidebar
              isOpen={isSidebarOpen}
              requestConfig={requestConfig}
              setRequestConfig={setRequestConfig}
              availableFields={standardAvailableFields}
              keywordWeight={keywordWeight}
              setKeywordWeight={setKeywordWeight}
              handleSendWeight={handleUpdateKeywordWeight}
              showSuccess={showSuccess}
            />

            <button
              className="toggle-btn"
              onClick={toggleSidebar}
              aria-label="Sidebar umschalten"
            >
              {isSidebarOpen ? '←' : '→'}
            </button>

            <SearchFeatureContainer requestConfig={requestConfig} keywordWeight={keywordWeight}/>
            <Footer />
          </>
        )}
      </div>
    </ConfigProvider>
  );
}

export default App;